import { useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { Drawer } from "@/components/ui/Modal.jsx";
import Button from "@/components/ui/Button.jsx";
import { Field, Input, Select, SwitchRow } from "@/components/ui/Form.jsx";
import { Callout } from "@/components/ui/Display.jsx";
import { ProductivityTag } from "@/components/ui/Tag.jsx";
import PatternTester from "./PatternTester.jsx";
import { PRODUCTIVITY } from "@/lib/tokens.js";
import { RULE_TYPES, validatePattern } from "@/lib/productivityRules.js";
import { cx } from "@/lib/cx.js";

/*
 * Create / edit a rule.
 *
 *   create  POST  /api/productivity-rules       { name, type, pattern, isRegex,
 *                                                 globalStatus, teamRules, category }
 *   edit    PATCH /api/productivity-rules/:id   the same
 *
 * Team overrides get their own drawer: choosing a status for six teams is a
 * different job from writing a pattern, and mixing them means every rename
 * re-sends the whole override list.
 *
 * The form REFUSES to save an uncompilable regex. The backend would accept it
 * happily — its matcher catches the error, logs it and moves on — so the rule
 * would sit in the table looking healthy while classifying nothing.
 */
const schema = z.object({
    name: z.string().trim().min(1, "Please enter a name"),
    type: z.enum(["domain", "website", "application"]),
    pattern: z.string().trim().min(1, "Please enter a pattern"),
    isRegex: z.boolean().default(false),
    globalStatus: z.enum(["productive", "neutral", "unproductive", "unrated"]),
    category: z.string().trim().default(""),
});

const TYPE_HINT = {
    domain: "Matched against the hostname, e.g. youtube.com",
    website: "Matched against the full URL, e.g. https://youtube.com/watch?v=…",
    application: "Matched against the application name the desktop app reports",
};

export default function RuleFormDrawer({ rule, rules = [], onClose, onSubmit, saving, error }) {
    const editing = Boolean(rule?._id);
    const [status, setStatus] = useState(rule?.globalStatus ?? "unrated");

    const {
        register,
        handleSubmit,
        control,
        setError,
        formState: { errors },
    } = useForm({
        resolver: zodResolver(schema),
        mode: "onSubmit",
        reValidateMode: "onChange",
        defaultValues: {
            name: rule?.name ?? "",
            type: rule?.type ?? "application",
            pattern: rule?.pattern ?? "",
            isRegex: Boolean(rule?.isRegex),
            globalStatus: rule?.globalStatus ?? "unrated",
            category: rule?.category ?? "",
        },
    });

    const pattern = useWatch({ control, name: "pattern" });
    const isRegex = useWatch({ control, name: "isRegex" });
    const type = useWatch({ control, name: "type" });

    const draft = {
        _id: rule?._id,
        type: type ?? "application",
        pattern: pattern ?? "",
        isRegex: Boolean(isRegex),
        globalStatus: status,
        teamRules: rule?.teamRules ?? [],
    };

    const submit = (values) => {
        // Belt and braces: zod cannot express "compiles as a regex" without
        // knowing isRegex, and this is the one mistake that fails silently.
        const valid = validatePattern(values.pattern, values.isRegex);
        if (!valid.ok) {
            setError("pattern", { message: valid.message });
            return;
        }
        onSubmit({ ...values, globalStatus: status });
    };

    return (
        <Drawer
            open
            onClose={onClose}
            title={editing ? "Edit rule" : "New rule"}
            subtitle={editing ? rule.name : "Classify an app or site as productive or not"}
            size="lg"
            footer={
                <>
                    <Button onClick={onClose} disabled={saving}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        type="submit"
                        form="rule-form"
                        loading={saving}
                        data-save-rule
                    >
                        {editing ? "Save changes" : "Create rule"}
                    </Button>
                </>
            }
        >
            <form id="rule-form" onSubmit={handleSubmit(submit)} noValidate className="space-y-4">
                {error && <Callout tone="danger">{error}</Callout>}

                <Field label="Name" htmlFor="r-name" error={errors.name?.message}>
                    <Input id="r-name" data-autofocus autoComplete="off" {...register("name")} />
                </Field>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Field
                        label="Type"
                        htmlFor="r-type"
                        error={errors.type?.message}
                        hint={TYPE_HINT[type ?? "application"]}
                    >
                        <Select id="r-type" {...register("type")}>
                            {RULE_TYPES.map((t) => (
                                <option key={t} value={t}>
                                    {t.charAt(0).toUpperCase() + t.slice(1)}
                                </option>
                            ))}
                        </Select>
                    </Field>

                    <Field
                        label="Category"
                        htmlFor="r-category"
                        optional
                        hint="Only for grouping in the table."
                    >
                        <Input id="r-category" placeholder="Development" {...register("category")} />
                    </Field>
                </div>

                <Field label="Pattern" htmlFor="r-pattern" error={errors.pattern?.message}>
                    <Input id="r-pattern" className="font-mono" {...register("pattern")} />
                </Field>

                <SwitchRow
                    label="Regular expression"
                    hint="Off: the value must equal the pattern exactly. On: standard JavaScript regex, case-sensitive."
                    {...register("isRegex")}
                />

                <PatternTester draft={draft} rules={rules} />

                {/* ---- Status ---- */}
                <div>
                    <p className="mb-2 text-[11px] font-semibold tracking-widest text-slate-400 uppercase dark:text-slate-500">
                        Default status
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                        {Object.keys(PRODUCTIVITY).map((key) => (
                            <button
                                key={key}
                                type="button"
                                data-status={key}
                                aria-pressed={status === key}
                                onClick={() => setStatus(key)}
                                className={cx(
                                    "rounded-md border px-2.5 py-1.5 text-[12.5px] font-medium transition",
                                    status === key
                                        ? "border-brand-500 bg-brand-50 text-brand-700 dark:bg-brand-950 dark:text-brand-200"
                                        : "border-slate-200 text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800",
                                )}
                            >
                                <ProductivityTag status={key} />
                            </button>
                        ))}
                    </div>
                    <p className="mt-2 text-[12px] text-slate-400 dark:text-slate-500">
                        Teams can be given a different status from{" "}
                        <strong className="font-semibold">Overrides</strong>.
                    </p>
                </div>
            </form>
        </Drawer>
    );
}
